/** 3D model rendering: a MapLibre custom layer that draws glTF meshes with
 *  three.js on top of the venue's extrusions.
 *
 *  Features of kind "model" carry a Point geometry plus
 *  props = { model: "<library name>", scale, rotation } and the usual
 *  base/floor_id. Everything else on the map stays plain fill-extrusion —
 *  three.js is only pulled in (dynamically, so it code-splits) when a venue
 *  actually places a model.
 *
 *  Coordinate handling goes through two MapLibre APIs rather than hand-rolled
 *  mercator maths, because the obvious matrices are subtly wrong:
 *
 *   - transform.getMatrixForModel(lngLat, altitude) maps a Y-up model in
 *     metres (i.e. exactly what glTF gives us) into map world space;
 *   - transform.getProjectionDataForCustomLayer() gives the camera matrix.
 *     The `defaultProjectionData` handed to render() is NOT interchangeable:
 *     its z column is [0,0,1,0], so altitude collapses and every model
 *     renders flat on the ground.
 *
 *  Each model is drawn in its own pass with its transform folded into the
 *  *camera* matrix, leaving the object itself at identity. That looks like a
 *  pointless indirection — the matrix product is the same either way — but it
 *  is not, in floating point. Put the transform on the object and the shader
 *  ends up adding a metre-scale offset (~3e-8 in mercator units) to a
 *  coordinate of ~0.5, whose float32 step is 6e-8: the whole model collapses
 *  onto one or two representable positions and renders as spikes. Folding it
 *  into the camera keeps vertices at metre scale, where float32 has precision
 *  to spare. The passes are a few hundred triangles each, so the cost is
 *  irrelevant next to getting it right.
 */
import { listVenueModels, modelUrl } from "../../api.js";

export const MODEL_LAYER_ID = "venue-models";

export const isModelFeature = (feature) =>
  feature?.properties?.kind === "model" && feature.geometry?.type === "Point";

export const hasModels = (featuresFC) =>
  (featuresFC?.features ?? []).some(isModelFeature);

/** Adds the model layer. Returns a controller ({ setFloor, sync, destroy }),
 *  or null when there is nothing to draw and `keepEmpty` was not set.
 *
 *  The viewer loads a venue once, so it omits `keepEmpty` and the layer is
 *  skipped entirely for model-free venues (no three.js download). The editor
 *  passes `keepEmpty` so the layer survives while the user adds/removes models,
 *  and drives it with `sync(featuresFC, floorId)` on every edit. */
export async function addModelLayer(
  map,
  featuresFC,
  floorId,
  { beforeId, keepEmpty = false, venueSlug = null, versions = null } = {},
) {
  if (!keepEmpty && !hasModels(featuresFC)) return null;

  // Dynamic import keeps three.js (~600 kB) out of the main bundle for the
  // many venues that never place a model.
  const [THREE, { GLTFLoader }] = await Promise.all([
    import("three"),
    import("three/examples/jsm/loaders/GLTFLoader.js"),
  ]);

  const scene = new THREE.Scene();
  const camera = new THREE.Camera();

  // Flat-ish lighting: these are low-poly solids meant to read like the
  // extrusions next to them, not to look photographic.
  scene.add(new THREE.AmbientLight(0xffffff, 2.0));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(-0.5, -1, 1).normalize();
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.9);
  fill.position.set(1, 0.6, 0.4).normalize();
  scene.add(fill);

  // Each model needs a version for its URL, so a regenerated built-in is never
  // served from a stale cache. The viewer fetches its venue's list; the editor
  // already holds the admin catalogue and passes it in rather than asking
  // again. Neither falls back to a global list of every upload on the server
  // (item 0097). Failing to get versions is survivable: uploads are named by
  // content hash and built-ins revalidate on an ETag, so an unversioned URL
  // still resolves to the right bytes.
  const versionOf =
    versions ??
    (venueSlug
      ? await listVenueModels(venueSlug)
          .then((rows) => Object.fromEntries(rows.map((m) => [m.name, m.version])))
          .catch(() => ({}))
      : {});

  const loader = new GLTFLoader();
  const gltfCache = new Map(); // model name -> Promise<THREE.Group>
  const placed = new Map(); // feature id -> { object, local, floorId, onFloor, sig }
  let renderer = null;
  let destroyed = false;

  const loadModel = (name) => {
    if (!gltfCache.has(name)) {
      gltfCache.set(
        name,
        new Promise((resolve, reject) => {
          loader.load(modelUrl(name, versionOf[name]), (gltf) => resolve(gltf.scene), undefined, reject);
        }).catch((e) => {
          console.warn(`model "${name}" failed to load`, e);
          return null;
        }),
      );
    }
    return gltfCache.get(name);
  };

  // What a placement depends on: re-place only when one of these changes, so
  // an unrelated edit elsewhere on the map doesn't reload every mesh.
  function signature(feature) {
    const p = feature.properties ?? {};
    const [lng, lat] = feature.geometry.coordinates;
    return JSON.stringify([p.model, lng, lat, p.scale ?? 1, p.rotation ?? 0, p.base ?? 0, p.floor_id ?? null]);
  }

  // Clones share geometry+material with their cached template, so removing one
  // must NOT dispose those — only detach from the scene.
  const detach = (object) => object && scene.remove(object);

  function placeOrUpdate(feature) {
    const id = feature.id;
    const sig = signature(feature);
    const existing = placed.get(id);
    if (existing && existing.sig === sig) return Promise.resolve();

    const { model, scale = 1, rotation = 0 } = feature.properties ?? {};
    const [lng, lat] = feature.geometry.coordinates;
    const altitude = Number(feature.properties.base ?? 0);
    const size = Number(scale) || 1;
    const floor = feature.properties.floor_id;

    if (existing) detach(existing.object);

    // getMatrixForModel maps model metres -> mercator at this location; it
    // depends only on position and latitude, so it is computed once per edit
    // rather than per frame. The transform rides on the camera at draw time
    // (see the precision note at the top of this file).
    const local = new THREE.Matrix4()
      .fromArray(map.transform.getMatrixForModel([lng, lat], altitude))
      .multiply(new THREE.Matrix4().makeScale(size, size, size))
      // Map bearings run clockwise from north; three.js Y-rotation is
      // counter-clockwise, hence the negation.
      .multiply(new THREE.Matrix4().makeRotationY(-(Number(rotation) || 0) * (Math.PI / 180)));

    const entry = {
      object: null,
      local,
      floorId: floor === undefined || floor === null ? null : floor,
      onFloor: true,
      sig,
    };
    placed.set(id, entry);
    if (!model) return Promise.resolve();

    return loadModel(model).then((template) => {
      if (destroyed) return;
      // A newer sync may have superseded this exact entry in the meantime.
      if (placed.get(id) !== entry || !template) return;
      const object = template.clone(true);
      object.matrixAutoUpdate = false;
      object.matrix.identity();
      object.visible = false; // render() shows one model at a time
      scene.add(object);
      entry.object = object;
      entry.onFloor = entry.floorId == null || entry.floorId === currentFloor;
      map.triggerRepaint();
    });
  }

  let currentFloor = floorId;
  function applyFloor(activeFloorId) {
    currentFloor = activeFloorId;
    for (const entry of placed.values()) {
      // No floor_id = outdoor decoration, always visible (same rule the
      // fill-extrusion floor filter uses).
      entry.onFloor = entry.floorId == null || entry.floorId === activeFloorId;
    }
  }

  /** Reconcile the placed set to `featuresFC` — add new models, drop removed
   *  ones, re-transform changed ones. Cheap when nothing changed (signature
   *  match short-circuits). */
  function sync(featuresFC, activeFloorId) {
    if (activeFloorId !== undefined) currentFloor = activeFloorId;
    const models = (featuresFC?.features ?? []).filter(isModelFeature);
    const wanted = new Set(models.map((f) => f.id));
    for (const [id, entry] of placed) {
      if (!wanted.has(id)) {
        detach(entry.object);
        placed.delete(id);
      }
    }
    const jobs = models.map(placeOrUpdate);
    applyFloor(currentFloor);
    map.triggerRepaint();
    return Promise.all(jobs);
  }

  const layer = {
    id: MODEL_LAYER_ID,
    type: "custom",
    renderingMode: "3d",

    onAdd(_map, gl) {
      renderer = new THREE.WebGLRenderer({
        canvas: _map.getCanvas(),
        context: gl,
        antialias: true,
      });
      // MapLibre owns the frame; three.js must not wipe what it drew.
      renderer.autoClear = false;
    },

    render(gl, args) {
      if (!renderer) return;
      // Must be the custom-layer projection data — see the note at the top of
      // this file. The fallbacks cover older MapLibre versions, which passed
      // the matrix array directly.
      const matrix =
        map.transform?.getProjectionDataForCustomLayer?.()?.mainMatrix ??
        (Array.isArray(args) ? args : args?.defaultProjectionData?.mainMatrix);
      if (!matrix) return;

      const base = new THREE.Matrix4().fromArray(matrix);
      renderer.resetState();
      for (const entry of placed.values()) {
        if (!entry.onFloor || !entry.object) continue;
        camera.projectionMatrix.multiplyMatrices(base, entry.local);
        entry.object.visible = true;
        renderer.render(scene, camera);
        entry.object.visible = false;
      }
    },

    onRemove() {
      // The renderer shares the map's canvas/context — disposing it would take
      // the map down with it. Release only what we allocated.
      scene.traverse((node) => {
        node.geometry?.dispose?.();
        const material = node.material;
        if (Array.isArray(material)) material.forEach((m) => m.dispose?.());
        else material?.dispose?.();
      });
      renderer = null;
    },
  };

  map.addLayer(layer, beforeId);

  await sync(featuresFC, floorId);

  if (import.meta.env.DEV) window.__models = { scene, camera, placed, THREE };

  return {
    setFloor: applyFloor,
    sync,
    get count() {
      return placed.size;
    },
    destroy() {
      destroyed = true;
      if (map.getLayer(MODEL_LAYER_ID)) map.removeLayer(MODEL_LAYER_ID);
    },
  };
}
